import { getOfferPhase, type ExclusiveOffer, type OfferPhase } from '@workspace/global-utils';
import { useEffect, useState } from 'react';
import { OFFER_COUNTDOWN_TICK_MS } from '@workspace/ui/lib/upgrade-offer';

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export interface OfferCountdown {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  phase: OfferPhase;
}

// Ticks once a second until the offer ends. With no offer it reads as ended.
export function useOfferCountdown(
  offer: Pick<ExclusiveOffer, 'endsAt' | 'lastCallHours'> | null | undefined,
): OfferCountdown {
  const endTime = offer ? new Date(offer.endsAt).getTime() : 0;
  const [now, setNow] = useState(() => Date.now());

  const remaining = Math.max(0, endTime - now);
  const isRunning = remaining > 0;

  useEffect(() => {
    if (!isRunning) return;
    // `now` may be stale if the offer arrived after mount (e.g. a modal opening later).
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), OFFER_COUNTDOWN_TICK_MS);
    return () => window.clearInterval(timer);
  }, [isRunning, endTime]);

  return {
    days: Math.floor(remaining / DAY),
    hours: Math.floor((remaining % DAY) / HOUR),
    minutes: Math.floor((remaining % HOUR) / MINUTE),
    seconds: Math.floor((remaining % MINUTE) / SECOND),
    phase: offer ? getOfferPhase(offer, now) : 'ended',
  };
}
